import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { X } from 'lucide-react';

import { useT } from '../../i18n';
import { safeId } from './labelledControl';

// ─────────────────────────────────────────────────────────────────────
// A DIALOG THAT ACTUALLY BEHAVES LIKE ONE (#781 slice 9).
//
// There are 66 `fixed inset-0` overlays across 51 files in this app. Thirteen
// carry `role="dialog"`, ten carry `aria-modal`, and `aria-labelledby` appears
// ZERO times repo-wide — so even the marked-up ones are unnamed. Nothing in the
// codebase traps focus or restores it: `focusTrap|trapFocus|inert|FocusLock|
// tabbable` has no hits, and `document.activeElement` has none either. Three
// of the 51 files lock body scroll. At one site `aria-modal` sits on the
// BACKDROP — the div that also closes on click — so the thing declared modal
// includes the surface you dismiss it with.
//
// What that adds up to for a keyboard or screen-reader user: an overlay opens
// and focus stays behind it, Tab walks through the page underneath, Escape does
// nothing, and closing it drops focus to the top of the document. This
// component is the one place that is fixed, so the 60-odd call sites become a
// mechanical migration rather than 60 bespoke judgements.
//
// ── THE SEMANTICS ARE CONFIRMBUTTON'S, GENERALISED ───────────────────
//
// `ConfirmButton` (#589) already got the two hard parts right for the inline
// case, and its reasoning is the reasoning here:
//
//   • MOVE FOCUS TO THE NEW THING, or a screen reader never announces it and a
//     keyboard user is left on a trigger whose meaning silently changed.
//   • ESCAPE MUST WORK. «A confirmation with no way out but yes is a trap» —
//     and so is a modal with no way out but the mouse.
//
// What a modal adds over an inline question is the other two halves of the
// same idea: while it is open, the page behind it is not reachable (focus
// containment + scroll lock), and when it closes, focus goes BACK to whatever
// opened it rather than to nowhere.
//
// ── THE NAME IS REQUIRED, AT THE TYPE LEVEL AND AT RUNTIME ───────────
//
// `DialogProps` is a union of three named variants, so a `<Dialog>` with no
// name does not compile. It also THROWS, which is not redundant: half this
// codebase's components still take implicitly-`any` props, a call site reached
// through one of those is unchecked, and the failure mode we are fixing is
// precisely a dialog that looks right and announces nothing. A crash in dev is
// loud; an unnamed dialog is silent, which is how we got here. The repo already
// prefers the loud version — `useLocale` throws rather than falling back.
//
// ── WHAT THIS DELIBERATELY DOES NOT DO ───────────────────────────────
//
//   • NO PORTAL. Every existing overlay renders in place with `fixed inset-0`,
//     and adding a portal would change stacking and event propagation at 60
//     call sites during a migration whose whole point is that nothing visible
//     moves.
//   • NO `inert` / `aria-hidden` ON THE REST OF THE PAGE. Both need a single
//     known app root to apply to; the overlays render inside arbitrary
//     subtrees. Tab containment covers the keyboard, which is the reachability
//     that matters here. Marking the background hidden is its own slice.
//   • NESTED DIALOGS are not a supported shape. The handler only acts when
//     focus is inside its own panel (or nowhere), so two SIBLING dialogs do not
//     fight; a dialog rendered inside another one's children is untested.
// ─────────────────────────────────────────────────────────────────────

/**
 * Elements the browser would put in the tab order.
 *
 * VISIBILITY IS NOT FILTERED, and that is a deliberate limit rather than an
 * oversight: deciding whether an element is visible needs layout
 * (`offsetParent`, client rects), jsdom has no layout, so a visibility filter
 * would be code that only ever runs unverified. `disabled`, `type="hidden"`,
 * the `hidden` attribute and `aria-hidden` are all checked, because those are
 * attributes rather than geometry. A panel that hides a focusable element with
 * `display:none` in CSS can still receive a stop on the way round — a real
 * browser is the only place that can be seen.
 */
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true',
  );
}

// ── BODY SCROLL LOCK, REFERENCE-COUNTED ──────────────────────────────
//
// Counted rather than set-and-restore because two overlays can overlap in this
// app (a confirm inside a modal), and the inner one unmounting would otherwise
// hand scrolling back while the outer one is still up.
let lockCount = 0;
let overflowBeforeLock = '';

/**
 * Open dialogs, innermost last. Keyboard ownership belongs to the LAST one.
 *
 * ── WHY A STACK AND NOT A FOCUS TEST (#813 review) ─────────────────
 *
 * The first version decided ownership by asking where focus was:
 *
 *   const mine = active === null || active === document.body || panel.contains(active);
 *   if (!mine) return;
 *
 * Two failures followed from that, both reproduced:
 *
 *   1. ONE DIALOG, FOCUS OUTSIDE. If anything moved focus to a background
 *      element while the dialog was open — a programmatic `focus()`, another
 *      component's effect, a competing overlay — then `mine` was false and the
 *      next Tab was NOT prevented. Focus walked the page behind the dialog,
 *      which is the one thing a focus trap exists to stop. It matters more
 *      here because the background is deliberately not `inert`.
 *   2. TWO SIBLING DIALOGS, FOCUS ON BODY. Both handlers are on `document` in
 *      capture, so they run in registration order — the EARLIER-mounted one
 *      first. It saw `document.body`, claimed the event, and pulled focus into
 *      the UNDERLYING panel; the top dialog then saw focus elsewhere and
 *      returned. The bottom dialog won every time.
 *
 * Where focus happens to be is not evidence of whose event it is. The stack
 * makes ownership explicit: exactly one dialog — the innermost — handles a
 * given Tab or Escape, and it pulls focus into itself from wherever focus was.
 */
const openDialogs: symbol[] = [];

/**
 * Each open panel's way back: its opener, then its opener's own way back.
 *
 * ── WHY A CHAIN AND NOT JUST THE OPENER (#900) ─────────────────────
 *
 * A common shape in this app is a HAND-OFF: a button in one dialog closes it
 * and opens the next (a match's details → «Slett kamp» → the delete
 * confirmation; a KM player profile → a match row → the score entry). Both
 * happen in one commit, and the new dialog's opener is captured during its
 * render, while focus is still on the button in the OLD panel — which is gone
 * a moment later. Closing the new dialog then had nothing connected to give
 * focus back to, and it fell to `<body>`: the keyboard user is thrown to the
 * top of the document, which is the failure this component exists to fix.
 *
 * So a dialog whose opener sits inside another dialog's panel inherits that
 * panel's chain, and restoration walks it to the first element still in the
 * document — the control that opened the first dialog of the hand-off. A
 * WeakMap, so a panel that is gone takes its entry with it.
 */
const waysBack = new WeakMap<Element, Element[]>();

/** The app's modal card, capped to the screen and scrolling — see the panel. */
const DEFAULT_PANEL_CLASS = 'card-elev w-full max-w-md p-5 max-h-[92vh] overflow-y-auto';
const DEFAULT_PANEL_STYLE: CSSProperties = { maxHeight: 'calc(100dvh - 2rem)' };

export interface DialogBaseProps {
  /** Dismiss — Escape, the close control and the backdrop all call this. */
  onClose: () => void;
  /** The dialog's content. */
  children: ReactNode;
  /**
   * Panel classes. Defaults to the app's modal card, capped to the screen and
   * scrolling; pass to match a surface. A caller that passes its own classes
   * replaces the cap too, and has to give its panel one — a height limit and a
   * region that scrolls — or tall content is cut off at both ends (#1102).
   */
  className?: string;
  /** Backdrop classes. Defaults to the app's dimmed, blurred overlay. */
  backdropClassName?: string;
  /**
   * Inline panel style, for a surface whose look is written as `style` rather
   * than classes (the court-booking modals, #900). Pass `className=""` with it,
   * or the default card classes apply on top.
   *
   * WHY NOT CONVERT THOSE TO CLASSES, as the player app's sheets did: a dozen
   * booking modals carry fifteen-odd inline declarations each, and turning
   * each into an arbitrary-value class is a visual change nobody can review
   * line by line. Moving the same object onto the same element is one that
   * can be read at a glance.
   */
  style?: CSSProperties;
  /** Inline backdrop style — see `style`. Pass `backdropClassName=""` with it. */
  backdropStyle?: CSSProperties;
  /** Clicking the backdrop closes. Default `true` — the existing idiom. */
  closeOnBackdropClick?: boolean;
  /** Render the ✕. Default `true`. */
  showClose?: boolean;
  /** The ✕'s accessible name. Defaults to the catalog's «Lukk». */
  closeLabel?: string;
  /** Where focus lands on open. Defaults to the first focusable thing in the panel. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Id of an element describing the dialog, for `aria-describedby`. */
  describedBy?: string;
  /** `data-testid` for the backdrop and the panel, for a surface whose tests already used them. */
  testId?: { backdrop?: string; panel?: string };
}

/**
 * THE ACCESSIBLE NAME, AS THREE MUTUALLY EXCLUSIVE WAYS OF GIVING ONE.
 *
 * `heading` is the one to reach for: it renders the visible title the existing
 * modals already have AND names the dialog from it, which is why
 * `aria-labelledby` can go from zero to the common case without anyone having
 * to think about ids.
 */
export type DialogName =
  | {
      /** A visible title, rendered as the panel's `<h2>` and used as its name. */
      heading: ReactNode;
      label?: never;
      labelledBy?: never;
    }
  | {
      /** A name with no visible title — for a panel that has its own header. */
      label: string;
      heading?: never;
      labelledBy?: never;
    }
  | {
      /** The id of an element inside the panel that already reads as the title. */
      labelledBy: string;
      heading?: never;
      label?: never;
    };

export type DialogProps = DialogBaseProps & DialogName;

/**
 * A modal dialog: named, focus-trapped, escapable, and it gives focus back.
 *
 * ```tsx
 * {open && (
 *   <Dialog heading={t('players.edit.title')} onClose={() => setOpen(false)}>
 *     <Field label={t('players.common.name')}>
 *       <input className="input" value={name} onChange={…} />
 *     </Field>
 *   </Dialog>
 * )}
 * ```
 *
 * Mount it only while it is open — there is no `open` prop. Focus restoration
 * happens on unmount, which means «while open» is the component's whole
 * lifetime and the one state it can be in; an `open={false}` dialog that is
 * still mounted would have to re-implement all of it as a no-op.
 */
export function Dialog(props: DialogProps) {
  const {
    onClose,
    children,
    className,
    backdropClassName,
    style,
    backdropStyle,
    closeOnBackdropClick = true,
    showClose = true,
    closeLabel,
    initialFocus,
    describedBy,
    testId,
  } = props;
  const heading = 'heading' in props ? props.heading : undefined;
  const label = 'label' in props ? props.label : undefined;
  const labelledBy = 'labelledBy' in props ? props.labelledBy : undefined;

  const t = useT();
  const auto = useId();
  const headingId = safeId('dialog-heading', auto);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // WHO OPENED IT, captured during the FIRST render rather than in an effect.
  // At effect time the panel is already in the document; in practice focus has
  // not moved yet, but render happens strictly before the commit, so this is
  // the activeElement that the click or keypress left behind and not something
  // that merely usually is. Writing a ref is idempotent, so a double render
  // under StrictMode cannot overwrite it.
  const openerRef = useRef<Element[] | null>(null);
  if (openerRef.current === null && typeof document !== 'undefined') {
    const opener = document.activeElement;
    // The panel the opener sits in, if it is one of ours — see `waysBack`.
    const host = opener?.closest('[role="dialog"][aria-modal="true"]');
    openerRef.current = opener ? [opener, ...((host && waysBack.get(host)) ?? [])] : [];
  }

  // MOVE FOCUS IN. Falls back to the panel itself, which is why it is
  // `tabIndex={-1}`: a dialog with no focusable content still has to take focus
  // away from the page behind it, or Tab starts from wherever it was.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const target = initialFocus?.current ?? focusablesIn(panel)[0] ?? panel;
    target.focus();
  }, [initialFocus]);

  // GIVE IT BACK. Its own effect with no dependencies, so the cleanup runs on
  // UNMOUNT and not on every re-render that changes `onClose`. Guarded on
  // `isConnected` because the common case for a dialog is that confirming it
  // removed the row its trigger lived in.
  //
  // Walks the opener's chain (`waysBack`) to the first element still in the
  // document, so a dialog that took over from another one hands focus to
  // whatever opened the first, not to `<body>`.
  useEffect(() => {
    const panel = panelRef.current;
    if (panel) waysBack.set(panel, openerRef.current ?? []);
    return () => {
      const back = (openerRef.current ?? []).find(
        (el): el is HTMLElement => el instanceof HTMLElement && el.isConnected,
      );
      back?.focus();
    };
  }, []);

  // KEYBOARD OWNERSHIP. Pushed on mount and removed on unmount, so the
  // innermost open dialog is always last — see `openDialogs`.
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('dialog');
  useEffect(() => {
    const id = idRef.current!;
    openDialogs.push(id);
    return () => {
      const at = openDialogs.lastIndexOf(id);
      if (at !== -1) openDialogs.splice(at, 1);
    };
  }, []);

  // SCROLL LOCK while open.
  useEffect(() => {
    if (lockCount === 0) {
      overflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
    return () => {
      lockCount -= 1;
      if (lockCount === 0) document.body.style.overflow = overflowBeforeLock;
    };
  }, []);

  // ESCAPE CLOSES, AND TAB STAYS INSIDE.
  //
  // ON THE DOCUMENT IN CAPTURE, not on the panel. A keydown on the panel only
  // fires while focus is in the panel — and the case that needs containment
  // most is the one where focus has already got out (a click on the backdrop
  // with `closeOnBackdropClick` off puts it on `<body>`, and from there Tab
  // would walk the page behind the dialog). Capture also puts this ahead of
  // handlers inside the panel.
  //
  // TAB IS IMPLEMENTED RATHER THAN CONSTRAINED: every Tab is prevented and the
  // next stop chosen from the panel's own list. That is what makes the cycle
  // testable without a browser, and it means containment does not depend on
  // sentinel elements at the edges. The cost is that a POSITIVE `tabindex`
  // inside a panel is ignored — order is DOM order. Nothing in this app uses
  // one, and `tabindex > 0` is a defect in its own right.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      // ONLY THE INNERMOST DIALOG ACTS. Not «is focus in my panel» — see
      // `openDialogs` for the two ways that answered wrongly. Every other
      // open dialog returns here, so one keypress has exactly one owner and
      // two Escapes cannot close two dialogs.
      if (openDialogs[openDialogs.length - 1] !== idRef.current) return;

      if (e.key === 'Escape') {
        // AN ESCAPE SOMETHING INSIDE HAS ALREADY CLAIMED IS NOT OURS (#900).
        // A transient state inside the panel — an armed `ConfirmButton`, an
        // open number pad — owns the first Escape: it closes that, and only
        // the next one closes the dialog. Handlers inside the panel run AFTER
        // this capture listener, so such a component claims the key earlier
        // still, on `window` in capture, with `preventDefault()`.
        if (e.defaultPrevented) return;
        e.preventDefault();
        onClose();
        return;
      }

      // TAB IS ALWAYS PREVENTED AND ALWAYS LANDS IN THIS PANEL, wherever focus
      // was. That is what pulls focus back when something outside has taken
      // it: `at` is -1 for an element that is not in our list, so forward goes
      // to the first stop and backward to the last.
      const active = document.activeElement;
      const items = focusablesIn(panel);
      e.preventDefault();
      if (items.length === 0) {
        panel.focus();
        return;
      }
      // -1 for anything not in this panel's list, which is what makes the
      // pull-in work: an outside element simply is not in `items`, so forward
      // goes to the first stop and backward to the last. A `panel.contains`
      // test here would be dead logic — `indexOf` already answers it, and a
      // mutation removing it could not change the behaviour.
      const at = active instanceof HTMLElement ? items.indexOf(active) : -1;
      const next = e.shiftKey
        ? items[(at <= 0 ? items.length : at) - 1]
        : items[(at + 1) % items.length];
      next.focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // THE NAME IS NOT OPTIONAL. After the hooks so the hook order is the same on
  // every render of a given instance; during render, so nothing commits.
  if (heading === undefined && label === undefined && labelledBy === undefined) {
    throw new Error(
      '<Dialog> requires an accessible name: pass `heading`, `label` or `labelledBy`.',
    );
  }

  const header = heading !== undefined || showClose;

  return (
    // The backdrop's click is a pointer dismissal, not a control — see its onClick.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={
        backdropClassName ??
        'fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm'
      }
      style={backdropStyle}
      data-testid={testId?.backdrop}
      // The BACKDROP carries no dialog role — it is the dismiss surface, and a
      // `role="dialog"` that includes the thing you click to leave is why one
      // existing site declares a modal you can tap through.
      //
      // Nor is it a control, so it carries no role at all (#900): a click on it
      // only repeats Escape and the ✕, which are the keyboard's way out, and
      // its other job is to stop clicks at the dialog's edge. The dialog tests
      // pin «no role on the backdrop», so the two rules that want a role or a
      // key handler on anything clickable are switched off for this one
      // handler rather than satisfied with a role that would mean nothing (the
      // directive sits above the element, where the rules report).
      onClick={(e) => {
        // A CLICK INSIDE A MODAL STAYS INSIDE IT (#917 regression). Hand-built
        // overlays close on ANY click that reaches their backdrop, and some
        // render a second modal inside it (the series class settings render
        // PlayerEditModal there). The hand-built panels stopped their clicks;
        // this one did not, so typing into the inner dialog closed the outer
        // one. Stopped HERE, at the outermost element, so nothing the dialog
        // contains, nor its own backdrop, is heard by the page around it.
        // Handlers INSIDE the dialog still see every click: React delivers to
        // them first, and this only ends the bubbling at the dialog's edge.
        e.stopPropagation();
        // `e.target === e.currentTarget`: only a click on the backdrop itself
        // is a dismissal, never one that bubbled up from the panel.
        if (closeOnBackdropClick && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-labelledby={labelledBy ?? (heading !== undefined ? headingId : undefined)}
        aria-describedby={describedBy}
        data-testid={testId?.panel}
        tabIndex={-1}
        // THE DEFAULT PANEL FITS THE SCREEN (#1102). It used to have no height
        // limit, and a panel centred by its backdrop that is taller than the
        // screen is cut off at BOTH ends with neither end reachable: the title
        // above the top, the action buttons below the bottom. Capped, and the
        // panel itself scrolls. The panel and not a body region, because the
        // actions live in `children` — there is no footer to keep in view, and
        // a scrolling region inside `p-5` would clip the focus rings of the
        // inputs that touch its edges. `92vh` is the floor for a browser
        // without `dvh`, which drops the inline value and keeps the class.
        //
        // ONLY THE DEFAULT. A caller that passes `className` owns its panel's
        // layout — several are already capped flex columns with a scrolling
        // body of their own, and a cap or a scroll forced onto those would
        // fight it. `style` still wins over the default cap.
        className={className ?? DEFAULT_PANEL_CLASS}
        style={className === undefined ? { ...DEFAULT_PANEL_STYLE, ...style } : style}
      >
        {header ? (
          <div
            className={`flex items-center gap-3 ${heading !== undefined ? 'justify-between mb-4' : 'justify-end mb-2'}`}
          >
            {heading !== undefined ? (
              <h2 id={headingId} className="font-display text-xl font-semibold">
                {heading}
              </h2>
            ) : null}
            {showClose ? (
              <button
                type="button"
                onClick={onClose}
                // A NAME AND A TARGET. An `<X/>` glyph in a button is an
                // unlabelled control, and 18px of icon is below the 24px
                // minimum target — so the name is explicit and the box is 28px
                // regardless of what the icon measures.
                aria-label={closeLabel ?? t('ui.dialog.close')}
                className="flex items-center justify-center w-7 h-7 -mr-1 rounded text-ink-3 hover:text-ink hover:bg-bg-soft transition-colors focus-ring"
              >
                <X size={18} />
              </button>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}
